import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { QueryState } from '../components/page';
import { useCreateTeam, useTenantTeams } from '../api/hooks';

export function TenantTeamsSection({ tenantId }: { tenantId: string }) {
  const { data: teams, isLoading, error } = useTenantTeams(tenantId);
  const createTeam = useCreateTeam(tenantId);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');

  // Older backends have no team endpoints. Keep the page functional and
  // simply hide the section when the listing 404s.
  if ((error as { response?: { status?: number } } | null)?.response?.status === 404) return null;

  const onCreate = async () => {
    if (!name.trim() || !displayName.trim()) return;
    try {
      await createTeam.mutateAsync({ name: name.trim(), displayName: displayName.trim() });
      setName('');
      setDisplayName('');
      setShowCreate(false);
      toast.success('Team created');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Creating team failed');
    }
  };

  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Users className="h-4 w-4" />
          Teams
        </h2>
        <Button size="sm" variant="outline" onClick={() => setShowCreate(v => !v)}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          New team
        </Button>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        Open a team to manage its members, principal investigators, and outpatient practices.
      </p>
      {showCreate && (
        <div className="mb-3 flex items-center gap-2 rounded-3xl border p-3">
          <Input
            placeholder="Name (slug), e.g. hautmedizin-saar"
            value={name}
            onChange={event => setName(event.target.value)}
            className="h-8"
          />
          <Input
            placeholder="Display name, e.g. Hautmedizin Saar"
            value={displayName}
            onChange={event => setDisplayName(event.target.value)}
            className="h-8"
          />
          <Button
            size="sm"
            disabled={!name.trim() || !displayName.trim() || createTeam.isPending}
            onClick={onCreate}
          >
            Create
          </Button>
        </div>
      )}
      <QueryState isLoading={isLoading} error={error} empty={(teams ?? []).length === 0}>
        <div className="rounded-3xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Team</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Members</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(teams ?? []).map(team => (
                <TableRow key={team.id}>
                  <TableCell className="font-medium">
                    <Link
                      to="/tenants/$tenantId/teams/$teamId"
                      params={{ tenantId, teamId: team.id }}
                      className="hover:underline"
                    >
                      {team.displayName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{team.slug}</TableCell>
                  <TableCell>
                    <Badge variant={team.status === 'active' ? 'default' : 'outline'}>
                      {team.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>{team.activeMemberCount} registered</div>
                    {(team.preassignedMemberCount ?? 0) > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {team.preassignedMemberCount} before signup
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      to="/tenants/$tenantId/teams/$teamId"
                      params={{ tenantId, teamId: team.id }}
                      className="text-sm hover:underline"
                    >
                      Manage team
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </div>
  );
}
